import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, DollarSign, TrendingUp, Megaphone, ExternalLink, Sparkles, ArrowRight, Search, Target, Crown, Copy, Check } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import { toast } from "sonner";

export default function PpcLeakPage() {
  const { active } = useProjects();
  const navigate = useNavigate();
  const [domain, setDomain] = useState("");
  const [limit, setLimit] = useState(50);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [recent, setRecent] = useState([]);
  const [adsTarget, setAdsTarget] = useState(null);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    api.get("/explorer/ppc-leak/recent").then(({ data }) => setRecent(data)).catch(() => {});
  }, []);

  const run = async (e) => {
    e?.preventDefault();
    const d = domain.trim();
    if (!d) { toast.error("Enter a competitor domain"); return; }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/explorer/ppc-leak", { competitor_domain: d, limit: Number(limit), project_id: active?.id });
      setResult(data);
      toast.success(`Found ${data.commercial_keywords.length} commercial keywords`);
      api.get("/explorer/ppc-leak/recent").then(({ data }) => setRecent(data)).catch(() => {});
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  const openRecent = async (leakId) => {
    try {
      const { data } = await api.get(`/explorer/ppc-leak/${leakId}`);
      setResult(data);
      setDomain(data.competitor_domain);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copyAll = () => {
    if (!result) return;
    const text = result.commercial_keywords.map(k => k.keyword).join("\n");
    navigator.clipboard.writeText(text);
    setCopied("all");
    setTimeout(() => setCopied(""), 1500);
    toast.success(`Copied ${result.commercial_keywords.length} keywords to clipboard`);
  };

  return (
    <DashboardLayout
      eyebrow="Competitive intel"
      title="Steal your competitor's paid keywords"
      subtitle="See which keywords your competitor is paying real money to rank on Google Ads, then one-click build a campaign to outbid them."
    >
      <form onSubmit={run} className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 mb-6" data-testid="ppc-leak-form">
        <div className="grid md:grid-cols-[1fr_140px_180px] gap-3 items-end">
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Competitor domain</Label>
            <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="e.g. ahrefs.com"
                   className="h-12 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="ppc-leak-domain" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Limit</Label>
            <Input type="number" min={10} max={200} value={limit} onChange={(e) => setLimit(e.target.value)}
                   className="h-12 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="ppc-leak-limit" />
          </div>
          <Button type="submit" disabled={loading} className="h-12 bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="ppc-leak-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-2" /> Find paid keywords</>}
          </Button>
        </div>
        <p className="text-xs text-zinc-500 mt-3 leading-relaxed">
          We pull real keyword data from DataForSEO Labs for any domain, filter for high commercial intent (CPC ≥ $0.5 or MEDIUM/HIGH competition), and rank by revenue potential (CPC × volume).
        </p>
      </form>

      {loading && (
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-16 text-center">
          <Loader2 className="w-7 h-7 animate-spin text-[#0055FF] mx-auto mb-3" />
          <p className="text-sm text-zinc-400">Scanning competitor's paid keyword universe…</p>
        </div>
      )}

      {result && !loading && (
        <div className="space-y-6 fade-in" data-testid="ppc-leak-result">
          <div className="border border-[#10B981]/30 bg-gradient-to-br from-[#10B981]/8 via-zinc-950 to-zinc-950 rounded-sm p-5">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="w-11 h-11 rounded-sm bg-[#10B981]/15 border border-[#10B981]/40 grid place-items-center flex-shrink-0">
                <Crown className="w-5 h-5 text-[#10B981]" />
              </div>
              <div className="flex-1 min-w-[260px]">
                <h2 className="font-display text-xl font-semibold tracking-tight">Stealing from <span className="text-[#10B981]">{result.competitor_domain}</span></h2>
                <p className="text-sm text-zinc-400 mt-1">Their top {result.commercial_keywords.length} highest-CPC keywords — ranked by revenue potential</p>
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={copyAll} variant="outline" className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid="ppc-copy-all">
                  {copied === "all" ? <><Check className="w-4 h-4 mr-2 text-[#10B981]" /> Copied</> : <><Copy className="w-4 h-4 mr-2" /> Copy all</>}
                </Button>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3 mt-5 pt-5 border-t border-zinc-800">
              <Kpi icon={DollarSign} label="Est. monthly paid spend" value={`$${(result.summary.monthly_paid_spend_estimate_usd || 0).toLocaleString()}`} />
              <Kpi icon={TrendingUp} label="Avg CPC" value={`$${result.summary.avg_cpc_usd}`} />
              <Kpi icon={Target} label="Keywords found" value={result.summary.keyword_count} />
            </div>
          </div>

          <div className="bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-zinc-800">
              <span className="label-eyebrow">Top commercial keywords</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                    <th className="px-6 py-3 font-normal">Keyword</th>
                    <th className="px-6 py-3 font-normal text-right">Volume</th>
                    <th className="px-6 py-3 font-normal text-right">CPC</th>
                    <th className="px-6 py-3 font-normal text-right">Difficulty</th>
                    <th className="px-6 py-3 font-normal text-right">Revenue potential</th>
                    <th className="px-6 py-3 font-normal text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {result.commercial_keywords.map((k, i) => {
                    const revPot = (k.cpc || 0) * (k.volume || 0);
                    return (
                      <tr key={i} className="border-b border-zinc-900 last:border-0 hover:bg-zinc-900/50 transition-colors" data-testid={`ppc-row-${i}`}>
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-2">
                            <span className="text-zinc-200">{k.keyword}</span>
                            {i < 5 && <Badge className="bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30 rounded-sm text-[10px]">TOP {i + 1}</Badge>}
                          </div>
                        </td>
                        <td className="px-6 py-3 text-right text-zinc-300 font-mono">{(k.volume || 0).toLocaleString()}</td>
                        <td className="px-6 py-3 text-right text-zinc-300 font-mono">${(k.cpc || 0).toFixed(2)}</td>
                        <td className="px-6 py-3 text-right">
                          <span className={`text-xs uppercase tracking-widest ${k.competition_level === "LOW" ? "text-[#10B981]" : k.competition_level === "HIGH" ? "text-red-400" : "text-amber-400"}`}>
                            {k.competition_level || "—"}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-right font-mono text-[#10B981]">${revPot.toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo</td>
                        <td className="px-6 py-3 text-right">
                          <button
                            onClick={() => setAdsTarget(k)}
                            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#10B981] hover:text-white bg-[#10B981]/10 hover:bg-[#10B981] border border-[#10B981]/30 hover:border-[#10B981] rounded-sm px-2 py-1 transition-colors"
                            data-testid={`steal-kw-${i}`}
                          >
                            <Megaphone className="w-3 h-3" /> Steal
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {!loading && !result && recent.length > 0 && (
        <section className="mt-6">
          <h3 className="text-xs tracking-widest uppercase text-zinc-500 mb-3">Recent analyses</h3>
          <ul className="space-y-2" data-testid="ppc-recent">
            {recent.map((r) => (
              <li key={r.id}
                  onClick={() => openRecent(r.id)}
                  className="bg-zinc-950 border border-zinc-800 hover:border-zinc-700 rounded-sm p-3 flex items-center gap-3 cursor-pointer transition-colors"
                  data-testid={`ppc-recent-${r.id}`}>
                <Crown className="w-4 h-4 text-zinc-500" />
                <span className="flex-1 truncate font-mono text-sm text-zinc-200">{r.competitor_domain}</span>
                <Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-500 text-[10px]">${r.summary?.avg_cpc_usd} avg CPC</Badge>
                <span className="text-xs text-zinc-500">{new Date(r.created_at).toLocaleDateString()}</span>
                <ArrowRight className="w-4 h-4 text-zinc-600" />
              </li>
            ))}
          </ul>
        </section>
      )}

      {!loading && !result && recent.length === 0 && (
        <div className="border border-dashed border-zinc-800 rounded-sm p-12 text-center text-zinc-500">
          <Crown className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
          <p>Enter a competitor's domain to see their highest-CPC keywords.</p>
        </div>
      )}

      {/* Steal dialog */}
      <StealDialog target={adsTarget} onClose={() => setAdsTarget(null)} navigate={navigate} />
    </DashboardLayout>
  );
}

function Kpi({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="w-4 h-4 text-zinc-500" />
      <div>
        <div className="text-[10px] tracking-widest uppercase text-zinc-500">{label}</div>
        <div className="font-display text-lg font-semibold">{value}</div>
      </div>
    </div>
  );
}

function StealDialog({ target, onClose, navigate }) {
  const [budget, setBudget] = useState(25);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => { if (target) { setBudget(25); setCreated(null); } }, [target]);

  const steal = async () => {
    if (!target) return;
    setCreating(true);
    try {
      const { data } = await api.post("/google-ads/from-gsc-query", {
        query: target.keyword,
        daily_budget_usd: Number(budget),
      });
      setCreated(data);
      toast.success("Campaign built — ready to bid against the competition");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  if (!target) return null;
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0B0B0E] border-zinc-800 rounded-sm sm:max-w-lg" data-testid="ppc-steal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-[#10B981]" />
            Steal this keyword
          </DialogTitle>
          <DialogDescription className="text-zinc-400 leading-relaxed">
            Build a Google Ads campaign targeting this exact keyword. We'll outbid the competitor with AI-optimised ad copy.
          </DialogDescription>
        </DialogHeader>

        {!created ? (
          <div className="space-y-4">
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4 space-y-1.5">
              <div className="text-xs text-zinc-500">Target keyword</div>
              <div className="font-mono text-sm text-zinc-100">"{target.keyword}"</div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                <div><div className="text-zinc-500">Volume</div><div className="font-mono text-zinc-200">{(target.volume || 0).toLocaleString()}</div></div>
                <div><div className="text-zinc-500">CPC</div><div className="font-mono text-zinc-200">${(target.cpc || 0).toFixed(2)}</div></div>
                <div><div className="text-zinc-500">Competition</div><div className="font-mono text-zinc-200">{target.competition_level || "—"}</div></div>
              </div>
            </div>
            <div>
              <Label className="text-[10px] tracking-widest uppercase text-zinc-500 mb-1.5 block">Daily budget (USD)</Label>
              <Input type="number" min={1} value={budget} onChange={(e) => setBudget(e.target.value)} className="bg-zinc-950 border-zinc-800 rounded-sm h-10" data-testid="ppc-steal-budget" />
              <p className="text-[10px] text-zinc-600 mt-1.5">Recommended for this CPC: at least ${Math.max(20, Math.ceil((target.cpc || 1) * 10))}/day</p>
            </div>
          </div>
        ) : (
          <div className="bg-zinc-950 border border-[#10B981]/40 rounded-sm p-4 space-y-3" data-testid="ppc-steal-success">
            <div className="flex items-center gap-2 text-[#10B981]">
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-medium">Campaign ready</span>
            </div>
            <div className="grid grid-cols-3 gap-3 text-xs">
              <div><div className="text-zinc-500">Ads</div><div className="font-display text-lg text-zinc-100">{created.ads?.length || 0}</div></div>
              <div><div className="text-zinc-500">Groups</div><div className="font-display text-lg text-zinc-100">{created.keyword_groups?.length || 0}</div></div>
              <div><div className="text-zinc-500">Est clicks/day</div><div className="font-display text-lg text-zinc-100">{created.estimated_clicks_per_day || 0}</div></div>
            </div>
          </div>
        )}

        <DialogFooter>
          {!created ? (
            <>
              <Button variant="outline" onClick={onClose} className="bg-zinc-950 border-zinc-800 rounded-sm">Cancel</Button>
              <Button onClick={steal} disabled={creating} className="bg-[#10B981] hover:bg-[#10B981]/90 text-[#062D24] rounded-sm" data-testid="ppc-steal-build">
                {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Building…</> : <><Megaphone className="w-4 h-4 mr-2" /> Steal it</>}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} className="bg-zinc-950 border-zinc-800 rounded-sm">Close</Button>
              <Button onClick={() => { onClose(); navigate("/google-ads"); }} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm">
                Open campaign <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
